// Screen: "Background Lab"
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Sample card rendering for the demo
const SUIT_COLORS = { "\u2660": "#1e293b", "\u2665": "#dc2626", "\u2666": "#dc2626", "\u2663": "#1e293b" };

function DemoCard({ rank, suit, small }) {
  const color = SUIT_COLORS[suit];
  const w = small ? 48 : 68;
  const h = small ? 70 : 100;
  return (
    <div style={{
      width: w, height: h, borderRadius: 7, background: "#fff",
      border: "2px solid #bbb", boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      position: "relative", flexShrink: 0, overflow: "hidden"
    }}>
      <img src="/BuckEuchreLogo.png" alt="" style={{
        position: "absolute", width: w * 1.9, height: "auto", opacity: 0.10, pointerEvents: "none", zIndex: 0
      }} />
      <div style={{ position: "absolute", top: 3, left: 4, fontSize: 13, fontWeight: 700, color, lineHeight: 1, zIndex: 1 }}>{rank}</div>
      <div style={{ fontSize: 32, color, fontWeight: 700, textShadow: `0 0 1px ${color}`, zIndex: 1 }}>{suit}</div>
      <div style={{ position: "absolute", bottom: 3, right: 4, fontSize: 13, fontWeight: 700, color, transform: "rotate(180deg)", lineHeight: 1, zIndex: 1 }}>{rank}</div>
    </div>
  );
}

function CardBack() {
  return (
    <div style={{
      width: 68, height: 100, borderRadius: 7,
      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%)",
      border: "2px solid #8b7355", boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{ width: 50, height: 80, borderRadius: 4, border: "1px solid rgba(255,255,255,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src="/BuckEuchreLogoWhite.png" alt="" style={{ width: 40, opacity: 0.4 }} />
      </div>
    </div>
  );
}

function Nameplate({ name, isDealer, trickCount, decidedToPlay }) {
  const borderColor = decidedToPlay ? "#22c55e" : "#334155";
  const borderWidth = decidedToPlay ? 3 : 2;
  return (
    <div style={{
      background: "#fff", borderRadius: 8, padding: "6px 12px",
      border: `${borderWidth}px solid ${borderColor}`, display: "inline-flex", alignItems: "center", gap: 8
    }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{name}</span>
      {isDealer && <span style={{ fontSize: 13, color: "#f59e0b" }}>D</span>}
      <span style={{ fontSize: 18, color: "#dc2626", fontWeight: 700 }}>{"\u2665"}</span>
      <div style={{ display: "flex", gap: 3 }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{
            width: 13, height: 13, borderRadius: 3,
            background: i < trickCount ? "#4ade80" : "#cbd5e1",
            border: "1px solid #94a3b8"
          }} />
        ))}
      </div>
    </div>
  );
}

// Dealer area / kitty with turned-up card and trump indicator
function DealerArea({ trump }) {
  const trumpSuit = trump || "\u2665";
  const trumpColor = SUIT_COLORS[trumpSuit];
  return (
    <div style={{
      width: 100, height: 155, background: "#f5f5f5", borderRadius: 8,
      border: `2px solid ${trumpColor}`, boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between",
      padding: 0, overflow: "hidden"
    }}>
      {/* Crib cards + turned up card */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", width: "100%" }}>
        {/* Stacked crib card backs */}
        <div style={{ position: "relative", width: 54, height: 76 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              position: "absolute", left: i * 2, top: i * 2,
              transform: `rotate(${i * 2}deg)`,
              width: 48, height: 70, borderRadius: 5,
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%)",
              border: "1.5px solid #8b7355", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
            }} />
          ))}
        </div>
        {/* Turned up card on top */}
        <div style={{ position: "absolute", zIndex: 5 }}>
          <DemoCard rank="Q" suit={trumpSuit} small />
        </div>
      </div>
      {/* Trump indicator */}
      <div style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
        borderTop: "1px solid rgba(0,0,0,0.15)", background: "#e8e8e8",
        borderRadius: "0 0 6px 6px", padding: "3px 0"
      }}>
        <span style={{ color: trumpColor, fontSize: 22, fontWeight: 700 }}>{trumpSuit}</span>
      </div>
    </div>
  );
}

// Scorecard panel (simplified)
function Scorecard() {
  return (
    <div style={{
      width: 290, background: "#fef9e7", borderRadius: 8,
      boxShadow: "0 4px 12px rgba(0,0,0,0.3)", border: "2px solid #d4af37",
      fontFamily: "'Courier New', monospace", fontSize: 13, color: "#222",
      overflow: "hidden", position: "relative"
    }}>
      <img src="/BuckEuchreLogo.png" alt="" style={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: 500, opacity: 0.15, pointerEvents: "none", zIndex: 0
      }} />
      <div style={{ padding: "12px 10px", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", fontWeight: 700, marginBottom: 8, fontSize: 15, borderBottom: "1px solid #888", paddingBottom: 4 }}>
          SCORECARD
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderBottom: "1px solid #888", paddingBottom: 4, marginBottom: 6, fontSize: 12 }}>
          <span style={{ width: "25%" }}>Hand</span>
          <span style={{ width: "25%", textAlign: "center" }}>You</span>
          <span style={{ width: "25%", textAlign: "center" }}>Slick Rick</span>
          <span style={{ width: "25%", textAlign: "center" }}>Lucky Lou</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, background: "#e8f4f8", padding: 4, borderRadius: 4, fontSize: 14 }}>
          <span style={{ width: "25%" }}>Total</span>
          <span style={{ width: "25%", textAlign: "center" }}>16</span>
          <span style={{ width: "25%", textAlign: "center" }}>19</span>
          <span style={{ width: "25%", textAlign: "center" }}>14</span>
        </div>
        <div style={{ marginTop: 6, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ width: "25%" }}>2</span>
            <span style={{ width: "25%", textAlign: "center", color: "green" }}>-5</span>
            <span style={{ width: "25%", textAlign: "center", color: "#666" }}>{"\u2014"}</span>
            <span style={{ width: "25%", textAlign: "center", color: "red" }}>+5</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ width: "25%" }}>1</span>
            <span style={{ width: "25%", textAlign: "center", color: "#666" }}>{"\u2014"}</span>
            <span style={{ width: "25%", textAlign: "center", color: "green" }}>-2</span>
            <span style={{ width: "25%", textAlign: "center", color: "green" }}>-2</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const BACKGROUNDS = [
  {
    name: "Classic Hardwood",
    style: {
      background: `
        repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
        repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
        linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
      `,
      backgroundBlendMode: "overlay, overlay, normal"
    }
  },
  {
    name: "Lake Dock",
    useImage: "/lakedock.jpg",
  },
  {
    name: "Green Felt Casino",
    style: {
      background: "radial-gradient(ellipse at center, #1a5c2e 0%, #0f4420 40%, #0a3318 70%, #072211 100%)",
    }
  },
  {
    name: "Bar / Pub",
    style: {
      background: `
        repeating-linear-gradient(90deg, #2a1810 0px, #3d2517 3px, #4a3020 6px, #3d2517 9px, #2a1810 12px),
        linear-gradient(180deg, #1a0f08 0%, #2a1810 30%, #3d2517 70%, #2a1810 100%)
      `,
      backgroundBlendMode: "overlay, normal"
    },
    overlay: "rgba(139, 69, 19, 0.15)"
  },
  {
    name: "City Night",
    style: {
      background: "linear-gradient(180deg, #0a0a1a 0%, #1a1a3e 20%, #2d2d5e 40%, #1a1a3e 60%, #3d2817 75%, #2d1f12 100%)",
    },
    overlay: "rgba(59, 130, 246, 0.08)"
  },
  {
    name: "Midnight Blue",
    style: {
      background: "radial-gradient(ellipse at 50% 40%, #1e3a5f 0%, #152a45 40%, #0d1b2a 80%)",
    }
  },
  {
    name: "Warm Amber Den",
    style: {
      background: `
        repeating-linear-gradient(90deg, #5c3d1a 0px, #6b4a25 2px, #7a5730 4px, #6b4a25 6px, #5c3d1a 8px),
        linear-gradient(180deg, #7a5730 0%, #5c3d1a 50%, #3d2817 100%)
      `,
      backgroundBlendMode: "overlay, normal"
    },
    overlay: "rgba(245, 158, 11, 0.08)"
  },
  {
    name: "Sunset Patio",
    style: {
      background: "linear-gradient(180deg, #ff7b54 0%, #ff9a76 15%, #ffd384 30%, #87ceeb 50%, #2d6a4f 65%, #5c4033 80%, #3d2817 100%)",
    },
    overlay: "rgba(0,0,0,0.3)"
  }
];

export default function BackgroundLabPage() {
  const [selectedBg, setSelectedBg] = useState(0);
  const navigate = useNavigate();
  const { user, signout } = useAuth();

  const bg = BACKGROUNDS[selectedBg];

  // Background style: either CSS gradients or a real image
  const bgStyle = bg.useImage
    ? {
        backgroundImage: `url(${bg.useImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : bg.style;

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", color: "#e2e8f0", fontFamily: "'Georgia', serif" }}>
      {/* Top control bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "10px 20px", background: "#161b22", borderBottom: "1px solid #30363d"
      }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "#f59e0b" }}>Background Lab</h2>
          <span style={{ color: "#6b7280", fontSize: 12 }}>Experiment with table backgrounds</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/home")} style={{
            background: "#4b5563", color: "#fff", border: "none", borderRadius: 6,
            padding: "8px 16px", fontSize: 13, cursor: "pointer"
          }}>Back to Home</button>
          {user && (
            <button onClick={() => { signout(); navigate('/'); }} style={{
              background: "transparent", color: "#8b949e", border: "1px solid #30363d",
              borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer"
            }}>Sign Out</button>
          )}
        </div>
      </div>

      {/* Background selector */}
      <div style={{
        display: "flex", gap: 8, padding: "12px 20px", overflowX: "auto",
        background: "#21262d", borderBottom: "1px solid #30363d"
      }}>
        {BACKGROUNDS.map((b, i) => (
          <button
            key={i}
            onClick={() => setSelectedBg(i)}
            style={{
              padding: "8px 16px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
              background: i === selectedBg ? "#f59e0b" : "#30363d",
              color: i === selectedBg ? "#000" : "#e2e8f0",
              border: i === selectedBg ? "2px solid #fbbf24" : "1px solid #444",
              fontWeight: i === selectedBg ? 700 : 400,
              fontSize: 13
            }}
          >
            {b.name}
          </button>
        ))}
      </div>

      {/* Preview area - full game table simulation */}
      <div style={{
        ...bgStyle,
        position: "relative",
        height: "calc(100vh - 110px)",
        overflow: "hidden"
      }}>
        {/* Optional overlay for non-image backgrounds */}
        {bg.overlay && (
          <div style={{
            position: "absolute", inset: 0,
            background: bg.overlay, zIndex: 1, pointerEvents: "none"
          }} />
        )}

        {/* Top bar simulation */}
        <div style={{
          position: "relative", zIndex: 2,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "7px 12px", background: "#161b22", borderBottom: "1px solid #30363d",
          height: 30
        }}>
          <span style={{ color: "#94a3b8", fontSize: 12 }}>Hand 3</span>
          <span style={{ color: "#dc2626", fontSize: 14, fontWeight: 700 }}>Trump: {"\u2665"}</span>
          <span style={{ color: "#4ade80", fontSize: 10, background: "#1e293b", borderRadius: 4, padding: "1px 6px", border: "1px solid #334155" }}>Play</span>
        </div>

        {/* Scorecard (left side) */}
        <div style={{ position: "absolute", left: 10, top: 50, zIndex: 20 }}>
          <Scorecard />
        </div>

        {/* Table content - offset to right to account for scorecard */}
        <div style={{
          position: "relative", zIndex: 2,
          height: "calc(100% - 44px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginLeft: 260
        }}>
          {/* Player 2 (top) */}
          <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", textAlign: "center", zIndex: 5 }}>
            <Nameplate name="Slick Rick" isDealer={false} trickCount={1} decidedToPlay />
            <div style={{ marginTop: 4, display: "flex", gap: 3, justifyContent: "center" }}>
              {[0,1,2,3].map(i => <div key={i}><CardBack /></div>)}
            </div>
          </div>

          {/* Player 1 (right) */}
          <div style={{ position: "absolute", right: 30, top: "50%", transform: "translateY(-50%)", textAlign: "center", zIndex: 5 }}>
            <Nameplate name="Lucky Lou" isDealer={true} trickCount={2} />
            <div style={{ marginTop: 4, display: "flex", gap: 3, justifyContent: "center" }}>
              {[0,1,2].map(i => <div key={i}><CardBack /></div>)}
            </div>
          </div>

          {/* Dealer area / Kitty (center) */}
          <div style={{ position: "absolute", top: "38%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 4 }}>
            <DealerArea trump={"\u2665"} />
          </div>

          {/* Center trick area - cards played */}
          <div style={{ position: "relative", width: 250, height: 200, zIndex: 6 }}>
            <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)" }}>
              <DemoCard rank="K" suit={"\u2665"} />
            </div>
            <div style={{ position: "absolute", right: 10, top: "55%", transform: "translateY(-50%)" }}>
              <DemoCard rank="A" suit={"\u2665"} />
            </div>
            <div style={{ position: "absolute", bottom: -10, left: "50%", transform: "translateX(-50%)" }}>
              <DemoCard rank="10" suit={"\u2660"} />
            </div>
          </div>

          {/* Player 0 (bottom - you) */}
          <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", textAlign: "center", zIndex: 5 }}>
            <Nameplate name="You" isDealer={false} trickCount={1} decidedToPlay />
            <div style={{ marginTop: 4, display: "flex", gap: 5, justifyContent: "center" }}>
              <DemoCard rank="J" suit={"\u2665"} />
              <DemoCard rank="J" suit={"\u2666"} />
              <DemoCard rank="A" suit={"\u2660"} />
              <DemoCard rank="K" suit={"\u2663"} />
            </div>
          </div>
        </div>

        {/* Background name overlay */}
        <div style={{
          position: "absolute", bottom: 20, right: 20, zIndex: 10,
          background: "rgba(0,0,0,0.7)", padding: "8px 16px", borderRadius: 8,
          color: "#fbbf24", fontSize: 16, fontWeight: 700
        }}>
          {bg.name}
        </div>
      </div>
    </div>
  );
}
